import type { Ref } from 'vue'
import { isPostingUrl } from '~/utils/postingUrl'

export interface PostingFromUrl {
  text: string
  title: string | null
  company: string | null
  url: string
  source: 'json-ld' | 'page'
}

/**
 * Lets a job posting field take the ad's link instead of its text: once the
 * field holds a lone URL, the posting is read from that page
 * (/api/job-posting/from-url) and put into the field, ready to check or edit.
 * If the page cannot be read (a login wall, an app with no HTML), the link
 * stays and the message asks for the text instead.
 *
 * `onLoaded` gets the whole result, for forms that also keep the link, the
 * company or the position.
 */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

export function usePostingFromUrl(posting: Ref<string>, options: { max?: number; onLoaded?: (result: PostingFromUrl) => void } = {}) {
  const { t } = useI18n()

  const isLoading = ref(false)
  const error = ref('')
  /** The page the text in the field came from, while it is still that text. */
  const loadedFrom = ref<string | null>(null)
  let loadedText = ''
  let request = 0
  let timer: ReturnType<typeof setTimeout> | null = null

  async function load(url: string) {
    const id = ++request
    isLoading.value = true
    error.value = ''
    try {
      const result = await $fetch<PostingFromUrl>('/api/job-posting/from-url', { method: 'POST', body: { url } })
      // The visitor changed the field while the page loaded: theirs wins.
      if (id !== request || posting.value.trim() !== url) return
      loadedText = options.max ? result.text.slice(0, options.max) : result.text
      posting.value = loadedText
      loadedFrom.value = result.url
      options.onLoaded?.(result)
    } catch (err) {
      if (id !== request) return
      const status = (err as { statusCode?: number }).statusCode
      const code = (err as { data?: { data?: { code?: string } } }).data?.data?.code
      error.value = status === 429
        ? t('postingUrl.errors.limit')
        : code === 'no-posting' || code === 'not-html'
          ? t('postingUrl.errors.noPosting')
          : code === 'site-blocked'
            ? t('postingUrl.errors.siteBlocked', { host: hostOf(url) })
            : code === 'invalid-url' || code === 'blocked-url'
              ? t('postingUrl.errors.invalid')
              : t('postingUrl.errors.failed')
    } finally {
      if (id === request) isLoading.value = false
    }
  }

  watch(posting, (value) => {
    if (timer) clearTimeout(timer)
    if (loadedFrom.value && value !== loadedText) loadedFrom.value = null
    const text = (value ?? '').trim()
    if (!isPostingUrl(text)) {
      // Typed past the link (or cleared it): whatever was loading is moot.
      if (isLoading.value) {
        request++
        isLoading.value = false
      }
      error.value = ''
      return
    }
    // A short pause, so a link typed by hand is only fetched once it is whole.
    timer = setTimeout(() => load(text.startsWith('www.') ? `https://${text}` : text), 400)
  })

  onScopeDispose(() => {
    if (timer) clearTimeout(timer)
    request++
  })

  return { isLoading, error, loadedFrom }
}
